import { AttributeOption, Coverage, CoverageOption, CoverageUnderlyingCoverage, Policy } from "@deathstar/types/northstar";
import { EquipmentDriverChangeDto } from "@deathstar/types/waypoint";
import { Checkbox } from "@deathstar/ui";
import { CircularProgress } from "@material-ui/core";
import moment from "moment";
import { useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import api from "../../api/api";
import { EquipmentTypeString } from "../../api/queries/equipment";
import { useAccountId } from "../../api/useAccountId";
import { PolicyWithBillingInfo } from "../policies/policies-list/PoliciesList";

const COMPANY_DRIVER_COVERAGES = [CoverageOption.Id.TRUCKERS_AUTO_LIABILITY];
const OWNER_OPERATOR_COVERAGES = [
    CoverageOption.Id.TRUCKERS_AUTO_LIABILITY,
    CoverageOption.Id.TRUCKERS_PHYSICAL_DAMAGE,
    CoverageOption.Id.OCCUPATIONAL_ACCIDENT,
    CoverageOption.Id.CONTINGENT_LIABILITY,
    CoverageOption.Id.NON_TRUCKING_LIABILITY,
];
const INDEPENDENT_CONTRACTOR_OPTIONAL_DRIVER_COVERAGES = [
    CoverageOption.Id.TRUCKERS_PHYSICAL_DAMAGE,
    CoverageOption.Id.OCCUPATIONAL_ACCIDENT,
    CoverageOption.Id.CONTINGENT_LIABILITY,
    CoverageOption.Id.NON_TRUCKING_LIABILITY,
];

function getAvailableCoverages(dataType: EquipmentTypeString | "drivers", ownerOperator: boolean, coverages: Coverage[]): Coverage[] {
    switch (dataType) {
        case "drivers":
            return coverages.filter((coverage) => {
                if (!ownerOperator) {
                    return (
                        coverage.coverageOptionId !== CoverageOption.Id.OCCUPATIONAL_ACCIDENT &&
                        coverage.coverageOptionId !== CoverageOption.Id.CONTINGENT_LIABILITY &&
                        coverage.coverageOptionId !== CoverageOption.Id.NON_TRUCKING_LIABILITY &&
                        !isIndependentContractorCoverage(coverage)
                    );
                }
                return coverages;
            });
        case "tractors":
        case "trailers":
            return coverages.filter((coverage) => {
                if (!ownerOperator) {
                    return !coverage.policy!.attributes.some(
                        (a) =>
                            a.attributeOptionId === AttributeOption.Id.OWNER_OPERATOR_WAYPOINT_PROGRAM ||
                            a.attributeOptionId === AttributeOption.Id.CONTRACTOR_BENEFITS_ADMIN
                    );
                }
                return true;
            });
        case "autos":
            return coverages;
        case "tools":
            return coverages;
    }
}

function isIndependentContractorCoverage(coverage: Coverage): boolean | undefined {
    return coverage.policy?.attributes.some(
        (attribute) =>
            attribute.attributeOptionId === AttributeOption.Id.CONTRACTOR_BENEFITS_ADMIN ||
            attribute.attributeOptionId === AttributeOption.Id.OWNER_OPERATOR_WAYPOINT_PROGRAM
    );
}

const TRACTOR_COVERAGES = [
    CoverageOption.Id.TRUCKERS_AUTO_LIABILITY,
    CoverageOption.Id.TRUCKERS_PHYSICAL_DAMAGE,
    CoverageOption.Id.MOTOR_TRUCK_CARGO,
    CoverageOption.Id.EXCESS_CARGO,
];
const TRAILER_COVERAGES = [CoverageOption.Id.TRUCKERS_AUTO_LIABILITY, CoverageOption.Id.TRUCKERS_PHYSICAL_DAMAGE];
const TOOLS_COVERAGES = [CoverageOption.Id.INLAND_MARINE];

export default function CoverageSelector({
    dataType,
    isEditing,
    coverageFilter,
}: {
    dataType: EquipmentTypeString | "drivers";
    isEditing?: boolean;
    coverageFilter?: (coverage: Coverage) => boolean;
}) {
    const form = useFormContext<{ coverages: EquipmentDriverChangeDto["coverages"]; ownerOperator: boolean }>();
    const accountId = useAccountId();
    const { data } = api.policies.useFind<(Coverage & { policy: Pick<Policy, "writingCompany"> })[]>(accountId!, "active", {
        select: (policies) => {
            return policies
                .flatMap((policy) =>
                    policy.coverages
                        .filter((coverage) => {
                            if (coverageFilter && !coverageFilter({ ...coverage, policyId: policy.id } as Coverage)) {
                                return false;
                            }

                            if (
                                coverage.underlyingCoverages?.some(
                                    (uc) => uc.description === CoverageUnderlyingCoverage.Description.GENERAL_LIABILITY
                                )
                            ) {
                                // do not allow adding units/drivers to XSGL
                                return false;
                            }

                            switch (dataType) {
                                case "tractors":
                                    return CoverageOption.getAvailableAttachments(coverage.coverageOptionId).tractors;
                                case "trailers":
                                    return CoverageOption.getAvailableAttachments(coverage.coverageOptionId).trailers;
                                case "autos":
                                    return CoverageOption.getAvailableAttachments(coverage.coverageOptionId).businessAutos;
                                case "tools":
                                    return CoverageOption.getAvailableAttachments(coverage.coverageOptionId).tools;
                                case "drivers":
                                    return (
                                        CoverageOption.getAvailableAttachments(coverage.coverageOptionId).drivers ||
                                        (coverage.coverageOptionId === CoverageOption.Id.TRUCKERS_PHYSICAL_DAMAGE &&
                                            policy.attributes.some(
                                                (attribute) =>
                                                    attribute.attributeOptionId === AttributeOption.Id.CONTRACTOR_BENEFITS_ADMIN ||
                                                    attribute.attributeOptionId === AttributeOption.Id.OWNER_OPERATOR_WAYPOINT_PROGRAM
                                            ))
                                    );
                                default:
                                    return false;
                            }
                        })
                        .map(
                            (coverage) =>
                                ({ ...coverage, policyId: policy.id, policy } as Coverage & {
                                    policy: PolicyWithBillingInfo;
                                })
                        )
                )
                .sort((a, b) => moment(a.policy.effectiveDate).unix() - moment(b.policy.effectiveDate).unix());
        },
    });

    useEffect(() => {
        if (data && !isEditing && !form.formState.touchedFields.coverages) {
            if (dataType === "drivers") {
                if (form.getValues("ownerOperator")) {
                    form.setValue(
                        "coverages",
                        data
                            .filter((c) => OWNER_OPERATOR_COVERAGES.includes(c.coverageOptionId))
                            .map((c) => ({ policyId: c.policyId, coverageOptionId: c.coverageOptionId })),
                        { shouldTouch: false }
                    );
                } else {
                    form.setValue(
                        "coverages",
                        data
                            .filter((c) => COMPANY_DRIVER_COVERAGES.includes(c.coverageOptionId))
                            .map((c) => ({ policyId: c.policyId, coverageOptionId: c.coverageOptionId })),
                        { shouldTouch: false }
                    );
                }
            } else if (dataType === "tractors") {
                form.setValue(
                    "coverages",
                    data
                        .filter((c) => TRACTOR_COVERAGES.includes(c.coverageOptionId))
                        .map((c) => ({ policyId: c.policyId, coverageOptionId: c.coverageOptionId })),
                    { shouldTouch: false }
                );
            } else if (dataType === "trailers") {
                form.setValue(
                    "coverages",
                    data
                        .filter((c) => TRAILER_COVERAGES.includes(c.coverageOptionId))
                        .map((c) => ({ policyId: c.policyId, coverageOptionId: c.coverageOptionId })),
                    { shouldTouch: false }
                );
            } else if (dataType === "autos") {
                form.setValue("coverages", [], { shouldTouch: false });
            } else if (dataType === "tools") {
                form.setValue(
                    "coverages",
                    data
                        .filter((c) => TOOLS_COVERAGES.includes(c.coverageOptionId))
                        .map((c) => ({ policyId: c.policyId, coverageOptionId: c.coverageOptionId })),
                    { shouldTouch: false }
                );
            }
        }
        // using `form` in the deps here causes an infinite rerender loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, dataType, form.setValue, form.getValues, form.formState.touchedFields.coverages, isEditing]);
    const isOwnerOperator = form.watch("ownerOperator");
    const previousIsOwnerOperator = useRef(isOwnerOperator);
    const coverageTargetLabel =
        dataType === "drivers" ? "driver" : dataType === "trailers" ? "trailer" : dataType === "tractors" ? "tractor" : "unit";
    const notApplicableDescription =
        dataType === "drivers" && !isOwnerOperator
            ? "These coverages do not apply to company drivers. They are only available for independent contractors."
            : "These coverages only apply to independent contractors.";
    const availableCoverages = useMemo(
        () => getAvailableCoverages(dataType, isOwnerOperator, data || []),
        [data, dataType, isOwnerOperator]
    );
    const requiredCoverageOptionIds = useMemo(() => {
        if (dataType === "drivers") {
            const requiredCoverageIds = [CoverageOption.Id.TRUCKERS_AUTO_LIABILITY];
            return new Set(requiredCoverageIds);
        }
        if (dataType === "tractors" || dataType === "trailers") {
            const requiredCoverageIds = [CoverageOption.Id.TRUCKERS_AUTO_LIABILITY, CoverageOption.Id.CONTINGENT_LIABILITY];
            return new Set(requiredCoverageIds);
        }
        return new Set<CoverageOption.Id>();
    }, [dataType]);
    const requiredCoverages = useMemo(
        () => availableCoverages.filter((coverage) => requiredCoverageOptionIds.has(coverage.coverageOptionId)),
        [availableCoverages, requiredCoverageOptionIds]
    );
    const availableOptionalCoverages = useMemo(
        () => availableCoverages.filter((coverage) => !requiredCoverageOptionIds.has(coverage.coverageOptionId)),
        [availableCoverages, requiredCoverageOptionIds]
    );
    const notApplicableCoverages = useMemo(
        () => (data || []).filter((coverage) => !availableCoverages.some((availableCoverage) => availableCoverage.id === coverage.id)),
        [data, availableCoverages]
    );
    const coverages = form.watch("coverages");
    useEffect(() => {
        const coverages = form.getValues("coverages");
        if (
            data &&
            !coverages.every((c) =>
                availableCoverages.some((ac) => ac.policyId === c.policyId && ac.coverageOptionId === c.coverageOptionId)
            )
        ) {
            // remove selected coverages that are no longer available
            form.setValue(
                "coverages",
                coverages.filter((c) =>
                    availableCoverages.some((ac) => ac.policyId === c.policyId && ac.coverageOptionId === c.coverageOptionId)
                ),
                { shouldTouch: true, shouldDirty: true }
            );
        }
    }, [availableCoverages, data, form]);
    useEffect(() => {
        if (!requiredCoverages.length) {
            return;
        }

        const selectedCoverages = coverages;
        let hasNormalizedChanges = false;
        const normalizedCoverages = selectedCoverages.map((coverage) => {
            if (
                requiredCoverageOptionIds.has(coverage.coverageOptionId) &&
                requiredCoverages.some(
                    (requiredCoverage) =>
                        requiredCoverage.policyId === coverage.policyId && requiredCoverage.coverageOptionId === coverage.coverageOptionId
                )
            ) {
                if (coverage.remove) {
                    hasNormalizedChanges = true;
                }
                return { ...coverage, remove: false };
            }

            return coverage;
        });

        const missingRequiredCoverages = requiredCoverages
            .filter(
                (requiredCoverage) =>
                    !normalizedCoverages.some(
                        (coverage) =>
                            coverage.policyId === requiredCoverage.policyId &&
                            coverage.coverageOptionId === requiredCoverage.coverageOptionId &&
                            !coverage.remove
                    )
            )
            .map((requiredCoverage) => ({
                policyId: requiredCoverage.policyId,
                coverageOptionId: requiredCoverage.coverageOptionId,
            }));

        if (!missingRequiredCoverages.length && !hasNormalizedChanges) {
            return;
        }

        form.setValue("coverages", [...normalizedCoverages, ...missingRequiredCoverages], {
            shouldTouch: false,
            shouldDirty: true,
        });
        // using `form` in the deps here causes an infinite rerender loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coverages, requiredCoverages, requiredCoverageOptionIds, form.setValue]);
    useEffect(() => {
        if (isEditing || (dataType !== "tractors" && dataType !== "trailers") || !data) {
            return;
        }

        const selectedCoverages = form.getValues("coverages");
        const independentContractorCoverages = availableCoverages.filter((coverage) => isIndependentContractorCoverage(coverage));
        const nonIndependentContractorPhysicalDamageCoverages = availableCoverages.filter(
            (coverage) =>
                coverage.coverageOptionId === CoverageOption.Id.TRUCKERS_PHYSICAL_DAMAGE && !isIndependentContractorCoverage(coverage)
        );

        let hasChanges = false;
        const normalizedCoverages = selectedCoverages
            .map((coverage) => {
                if (
                    nonIndependentContractorPhysicalDamageCoverages.some(
                        (nonIndependentContractorPhysicalDamageCoverage) =>
                            nonIndependentContractorPhysicalDamageCoverage.policyId === coverage.policyId &&
                            nonIndependentContractorPhysicalDamageCoverage.coverageOptionId === coverage.coverageOptionId
                    ) &&
                    coverage.remove
                ) {
                    hasChanges = true;
                    return { ...coverage, remove: false };
                }

                if (
                    isOwnerOperator &&
                    independentContractorCoverages.some(
                        (independentContractorCoverage) =>
                            independentContractorCoverage.policyId === coverage.policyId &&
                            independentContractorCoverage.coverageOptionId === coverage.coverageOptionId
                    ) &&
                    coverage.remove
                ) {
                    hasChanges = true;
                    return { ...coverage, remove: false };
                }

                return coverage;
            })
            .filter((coverage) => {
                if (!isOwnerOperator) {
                    return true;
                }

                const shouldUncheckPhysicalDamageCoverage = nonIndependentContractorPhysicalDamageCoverages.some(
                    (nonIndependentContractorPhysicalDamageCoverage) =>
                        nonIndependentContractorPhysicalDamageCoverage.policyId === coverage.policyId &&
                        nonIndependentContractorPhysicalDamageCoverage.coverageOptionId === coverage.coverageOptionId
                );

                if (shouldUncheckPhysicalDamageCoverage) {
                    hasChanges = true;
                    return false;
                }

                return true;
            });

        const targetCoveragesToCheck = isOwnerOperator ? independentContractorCoverages : nonIndependentContractorPhysicalDamageCoverages;
        const missingTargetCoverages = targetCoveragesToCheck
            .filter(
                (targetCoverage) =>
                    !normalizedCoverages.some(
                        (coverage) =>
                            coverage.policyId === targetCoverage.policyId &&
                            coverage.coverageOptionId === targetCoverage.coverageOptionId &&
                            !coverage.remove
                    )
            )
            .map((targetCoverage) => ({
                policyId: targetCoverage.policyId,
                coverageOptionId: targetCoverage.coverageOptionId,
            }));

        if (!hasChanges && !missingTargetCoverages.length) {
            return;
        }

        form.setValue("coverages", [...normalizedCoverages, ...missingTargetCoverages], {
            shouldTouch: false,
            shouldDirty: true,
        });
        // using `form` in the deps here causes an infinite rerender loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableCoverages, data, dataType, form.setValue, form.getValues, isEditing, isOwnerOperator]);
    useEffect(() => {
        if (isEditing || dataType !== "drivers" || !data) {
            previousIsOwnerOperator.current = isOwnerOperator;
            return;
        }

        if (previousIsOwnerOperator.current === isOwnerOperator) {
            return;
        }

        if (!isOwnerOperator) {
            previousIsOwnerOperator.current = isOwnerOperator;
            return;
        }

        const selectedCoverages = form.getValues("coverages");
        const autoCheckedCoverages = availableCoverages.filter((coverage) =>
            INDEPENDENT_CONTRACTOR_OPTIONAL_DRIVER_COVERAGES.includes(coverage.coverageOptionId)
        );
        const missingAutoCheckedCoverages = autoCheckedCoverages
            .filter(
                (coverage) =>
                    !selectedCoverages.some(
                        (selectedCoverage) =>
                            selectedCoverage.policyId === coverage.policyId &&
                            selectedCoverage.coverageOptionId === coverage.coverageOptionId &&
                            !selectedCoverage.remove
                    )
            )
            .map((coverage) => ({ policyId: coverage.policyId, coverageOptionId: coverage.coverageOptionId }));

        if (missingAutoCheckedCoverages.length) {
            form.setValue("coverages", [...selectedCoverages, ...missingAutoCheckedCoverages], {
                shouldTouch: false,
                shouldDirty: true,
            });
        }

        previousIsOwnerOperator.current = isOwnerOperator;
        // using `form` in the deps here causes an infinite rerender loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableCoverages, data, dataType, form.setValue, form.getValues, isEditing, isOwnerOperator]);
    useEffect(() => {
        if (!data || dataType !== "tractors" || form.formState.touchedFields.coverages) {
            return;
        }

        const selectedCoverages = form.getValues("coverages");
        const autoCheckedCoverages = availableCoverages.filter(
            (coverage) => coverage.coverageOptionId === CoverageOption.Id.MOTOR_TRUCK_CARGO
        );
        const missingAutoCheckedCoverages = autoCheckedCoverages
            .filter(
                (coverage) =>
                    !selectedCoverages.some(
                        (selectedCoverage) =>
                            selectedCoverage.policyId === coverage.policyId &&
                            selectedCoverage.coverageOptionId === coverage.coverageOptionId &&
                            !selectedCoverage.remove
                    )
            )
            .map((coverage) => ({ policyId: coverage.policyId, coverageOptionId: coverage.coverageOptionId }));

        if (!missingAutoCheckedCoverages.length) {
            return;
        }

        form.setValue("coverages", [...selectedCoverages, ...missingAutoCheckedCoverages], {
            shouldTouch: false,
            shouldDirty: true,
        });
        // using `form` in the deps here causes an infinite rerender loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableCoverages, data, dataType, form.formState.touchedFields.coverages, form.setValue, form.getValues]);

    form.register("coverages", { required: isEditing || "Please select at least one coverage" });

    const renderCoverageCheckbox = (coverage: Coverage, disabled = false) => (
        <Checkbox
            classes={{ input: "w-4 h-4", label: "peer-disabled:opacity-50" }}
            key={coverage.id}
            checked={coverages.some(
                (c) => c.policyId === coverage.policyId && c.coverageOptionId === coverage.coverageOptionId && !c.remove
            )}
            disabled={disabled}
            onChange={() => {
                if (disabled) {
                    return;
                }

                if (coverages.some((c) => c.policyId === coverage.policyId && c.coverageOptionId === coverage.coverageOptionId)) {
                    if (isEditing) {
                        form.setValue(
                            "coverages",
                            coverages.map((c) =>
                                c.policyId === coverage.policyId && c.coverageOptionId === coverage.coverageOptionId
                                    ? { ...c, remove: !c.remove }
                                    : c
                            ),
                            { shouldTouch: true, shouldDirty: true }
                        );
                    } else {
                        form.setValue(
                            "coverages",
                            coverages.filter(
                                (c) => !(c.policyId === coverage.policyId && c.coverageOptionId === coverage.coverageOptionId)
                            ),
                            {
                                shouldTouch: true,
                                shouldDirty: true,
                            }
                        );
                    }
                } else {
                    form.setValue(
                        "coverages",
                        [...coverages, { policyId: coverage.policyId, coverageOptionId: coverage.coverageOptionId }],
                        {
                            shouldTouch: true,
                            shouldDirty: true,
                        }
                    );
                }
            }}
            label={
                <div>
                    <p className="">
                        {CoverageOption.getMetadata(coverage.coverageOptionId).name}
                        {isIndependentContractorCoverage(coverage) && " (IC)"}
                    </p>
                    <p className="text-xs text-stone-500">
                        {coverage.policy!.writingCompany?.name} • {moment(coverage.policy!.effectiveDate).format("M/D/YY")}
                    </p>
                </div>
            }
        />
    );

    return (
        <div className="w-full space-y-2 pl-4">
            {data ? (
                data.length ? (
                    <div className="space-y-4">
                        {!!requiredCoverages.length && (
                            <div>
                                <p className="text-sm font-medium text-waypoint-blue-dark">Required Coverages</p>
                                <p className="text-xs text-stone-500">
                                    {dataType === "drivers"
                                        ? "These coverages are required for all drivers"
                                        : "These coverages are required for all tractors and trailers"}
                                </p>
                                <div className="mt-1 space-y-2">
                                    {requiredCoverages.map((coverage) => renderCoverageCheckbox(coverage, true))}
                                </div>
                            </div>
                        )}
                        {!!availableOptionalCoverages.length && (
                            <div>
                                <p className="text-sm font-medium text-waypoint-blue-dark">Available Coverages</p>
                                <p className="text-xs text-stone-500">Select the coverage to apply to this {coverageTargetLabel}</p>
                                <div className="mt-1 space-y-2">
                                    {availableOptionalCoverages.map((coverage) => renderCoverageCheckbox(coverage))}
                                </div>
                            </div>
                        )}
                        {!!notApplicableCoverages.length && (
                            <div>
                                <p className="text-sm font-medium text-waypoint-blue-dark">Not Applicable Coverages</p>
                                <p className="text-xs text-stone-500">{notApplicableDescription}</p>
                                <div className="mt-1 space-y-2">
                                    {notApplicableCoverages.map((coverage) => renderCoverageCheckbox(coverage, true))}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <p className="mt-4 text-stone-500">No available coverages</p>
                )
            ) : (
                <div className="flex items-center justify-center">
                    <CircularProgress size="2rem" />
                </div>
            )}
        </div>
    );
}
